import { useEffect, useMemo, useRef } from 'react';
import type { OnboardingFlow } from '../types';
import { getInitialCompletedHotspotKeys, getNewlyCompletedGroups } from '../utils';
import { useOnboardingShallowStore } from './use-onboarding-store';

interface UseRegisterOnboardingFlowArgs {
  /**
   * Onboarding flow configuration
   */
  flow: OnboardingFlow;
  /**
   * Controls whether this flow should be active in the onboarding store.
   * When false, the flow is explicitly unregistered.
   */
  enabled: boolean;
  isLoading?: boolean;
  /**
   * Groups already persisted as complete outside the onboarding store.
   *
   * These are used to:
   * - pre-complete all hotspots that belong to already-completed groups
   * - avoid calling onGroupComplete for groups that are already complete
   */
  completedGroups: string[];
  /**
   * Called when this hook detects that a new group has become complete.
   *
   * The caller is responsible for persisting that completion, for example by
   * updating user preferences, backend state, or local storage.
   */
  onGroupComplete: (group: string) => void;
}
/**
 * Registers an onboarding flow with the global onboarding store and keeps
 * group-level completion state in sync with completed hotspot state.
 *
 * This hook bridges two levels of onboarding state:
 *
 * 1. Hotspot-level state:
 *    Stored inside the onboarding store as completed hotspot keys.
 *
 * 2. Group-level state:
 *    Owned by the product/page using this hook, usually persisted outside
 *    the onboarding store.
 *
 * Responsibilities:
 * - register the flow when enabled
 * - unregister the flow when disabled or unmounted
 * - initialize completed hotspots from already-completed groups
 * - detect newly completed groups as hotspots are completed
 * - notify the consumer through onGroupComplete
 */
export function useRegisterOnboardingFlow({
  flow,
  enabled,
  isLoading = false,
  completedGroups,
  onGroupComplete,
}: UseRegisterOnboardingFlowArgs) {
  const { registerFlow, unregisterFlow, completedHotspots } = useOnboardingShallowStore(
    'registerFlow',
    'unregisterFlow',
    'completedHotspots'
  );

  const emittedGroupsRef = useRef<Set<string>>(new Set());

  /**
   * When a group is already completed, all hotspots in that group should start
   * as completed as well. This prevents completed onboarding work from showing
   * again after page reloads or remounts.
   */
  const initialCompletedHotspotKeys = useMemo(
    () => getInitialCompletedHotspotKeys(flow, completedGroups),
    [flow, completedGroups]
  );

  /**
   * Register this flow only after external completion state has loaded.
   *
   * Cleanup always unregisters the flow to avoid leaving stale hotspots in the
   * global onboarding store after this component unmounts.
   */
  useEffect(() => {
    if (isLoading) return;

    const emittedGroups = emittedGroupsRef.current;

    if (enabled) {
      registerFlow(flow, initialCompletedHotspotKeys);
    } else {
      emittedGroups.clear();
      unregisterFlow(flow.id);
    }

    return () => {
      emittedGroups.clear();
      unregisterFlow(flow.id);
    };

    /**
     * completedGroups is intentionally excluded.
     *
     * It should initialize hotspot completion when the flow registers, but it
     * should not re-register the flow every time a group is completed during
     * the current session.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, flow, isLoading, registerFlow, unregisterFlow]);

  /**
   * Watch hotspot completion and derive newly completed groups.
   *
   * A group can become complete in two ways:
   * - all required hotspots in that group are completed
   * - a completed hotspot explicitly completes one or more groups via completeGroups
   */
  useEffect(() => {
    // A disabled flow is not registered in the store, so it must not derive or emit group completions. Without this guard, every mounted consumer of this hook reacts to the shared completedHotspots set and calls onGroupComplete, which fires persistence (e.g. UpdateOnboardingStatus) for flows the user is  not actually in.
    if (!enabled) return;
    if (isLoading) return;

    const newlyCompletedGroups = getNewlyCompletedGroups({
      flow,
      completedHotspots,
      alreadyCompletedGroups: completedGroups,
    });

    newlyCompletedGroups.forEach((group) => {
      // Skip groups already emitted this session so an in-flight persist that has not yet reflected in completedGroups cannot cause repeated onGroupComplete calls.
      if (emittedGroupsRef.current.has(group)) return;

      emittedGroupsRef.current.add(group);
      onGroupComplete(group);
    });
  }, [enabled, completedHotspots, completedGroups, onGroupComplete, flow, isLoading]);
}
